import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useMarkAllNotificationsRead, useMarkNotificationRead, useUnreadNotificationCount } from '@/hooks/use-notifications-api'
import { useOpenTicketCount } from '@/hooks/use-ticket-counts'
import { useNotificationStore } from '@/store/notifications'
import { Bell, CheckCheck } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { NotificationItem } from './NotificationItem'

export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const { data: unreadData } = useUnreadNotificationCount()
  const recentNotifications = useNotificationStore((s) => s.recentNotifications)
  const storeUnreadCount = useNotificationStore((s) => s.unreadCount)
  const markReadMutation = useMarkNotificationRead()
  const markAllReadMutation = useMarkAllNotificationsRead()

  const unreadTickets = useOpenTicketCount()
  const notifCount = unreadData?.count ?? storeUnreadCount
  const totalBadge = notifCount + unreadTickets

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {totalBadge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-ghost-500 px-1 text-[10px] font-bold text-white">
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <h4 className="text-sm font-semibold">Notifications</h4>
          {notifCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs text-muted-foreground"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification items */}
        {recentNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Bell className="mb-2 h-8 w-8 opacity-30" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <ScrollArea className="max-h-85">
            <div className="py-1">
              {recentNotifications.slice(0, 10).map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  compact
                  onClick={(n) => {
                    if (!n.readAt) markReadMutation.mutate(n.id)
                    setOpen(false)
                  }}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="border-t px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground"
            onClick={() => {
              setOpen(false)
              navigate('/notifications')
            }}
          >
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
